import React, { memo } from 'react';
import { Grid, Button } from '@material-ui/core';
import { Formik } from 'formik';
import { DeepReadonly } from 'utility-types';
import _ from 'lodash';
import * as Yup from 'yup';

import {
  BaseDetailContainer,
  BaseDetailDispatchProps,
  BaseDetailStateProps,
  BaseDetailContainerWrappedComponentProps,
  BaseDetailProps,
  FormikRelationSelect,
  RelationSelectItem,
  FormikDatePicker
} from '../../components';
import { nameofFactory } from '../../utils/nameofFactory';
import {
  reservationCheckOutValidationSchema,
  reservationCheckOutLogOnValidationSchema,
  reservationCheckOutLogOffValidationSchema
} from './validations';
import {
  ReservationGuestCheckOutModel, ReservationRoomModel,
} from '../../api/interfaces';
import { getCheckOutInfoAsync, ReservationGuestCheckOutEditType, selectRoomsSelectItemsByGuestId } from '.';

export type ReservationGuestCheckOutDetailStateProps = {
  guestsSelectItems: DeepReadonly<RelationSelectItem[]>;
  roomsSelectItems: DeepReadonly<RelationSelectItem[]>;
  rooms: { [itemId: number]: DeepReadonly<ReservationRoomModel> };
  type: ReservationGuestCheckOutEditType;
  info: string;
} & BaseDetailStateProps<ReservationGuestCheckOutModel>;

export type ReservationGuestCheckOutDetailDispatchProps = {
  selectRoomsSelectItemsByGuestId: typeof selectRoomsSelectItemsByGuestId;
  getInfo: typeof getCheckOutInfoAsync.request;
} & BaseDetailDispatchProps<ReservationGuestCheckOutModel>;

export type ReservationGuestCheckOutDetailProps = {
} & BaseDetailProps<ReservationGuestCheckOutModel> & ReservationGuestCheckOutDetailStateProps & ReservationGuestCheckOutDetailDispatchProps;

type RenderFormProps = {
  roomsSelectItems: DeepReadonly<RelationSelectItem[]>;
  guestsSelectItems: DeepReadonly<RelationSelectItem[]>;
  rooms: { [itemId: number]: DeepReadonly<ReservationRoomModel> };
  type: ReservationGuestCheckOutEditType;
  getInfo: typeof getCheckOutInfoAsync.request;
  info: string;
} & BaseDetailContainerWrappedComponentProps<ReservationGuestCheckOutModel>;

const nameof = nameofFactory<ReservationGuestCheckOutModel>();

const RenderFormComponent = (props: RenderFormProps): React.ReactElement => {
  const {
    classes,
    errorMessage,
    requestInProgress,
    initialValues,
    guestsSelectItems,
    roomsSelectItems,
    rooms,
    cancelActionRequest,
    type,
    info
  } = props;


  const handleActionCancel = (): void => {
    console.debug('handleActionCancel');

    cancelActionRequest();
  };

  return (
    <Formik<ReservationGuestCheckOutModel>
      initialValues={initialValues}
      onSubmit={(values: ReservationGuestCheckOutModel, { setSubmitting }): void => {
        setSubmitting(true);
        props.saveActionRequest(values);
      }}
      validationSchema={(): any => Yup.lazy(() => {
        if (type === ReservationGuestCheckOutEditType.Logoff) {
          return reservationCheckOutLogOffValidationSchema;
        } else if (type === ReservationGuestCheckOutEditType.Logon) {
          return reservationCheckOutLogOnValidationSchema;
        }

        return reservationCheckOutValidationSchema;
      })}
    >
      {(formProps): React.ReactElement => {
        const { handleSubmit, setFieldValue, values } = formProps;
        const filteredRoomsSelectItems = values.guestId > 0 ? selectRoomsSelectItemsByGuestId(values.guestId, rooms) : roomsSelectItems;
        return (
          <form onSubmit={handleSubmit} autoComplete="off" style={{ margin: '12px' }}>
            <Grid container className={classes.grid} spacing={3}>
             {info && <Grid item xs={12}>{info}</Grid>}
              {(type === ReservationGuestCheckOutEditType.Logoff || type === undefined) &&
                <Grid item xs={12}>
                  <FormikDatePicker
                    iconName="address-card"
                    labelText="Odchod"
                    InputLabelProps={{
                      shrink: true
                    }}
                    name={nameof('from')}
                    showErrorWhenNotTouched={true}
                  />
                </Grid>}
              {(type === ReservationGuestCheckOutEditType.Logon || type === undefined) &&
                <Grid item xs={12}>
                  <FormikDatePicker
                    iconName="address-card"
                    labelText="Příchod"
                    InputLabelProps={{
                      shrink: true
                    }}
                    name={nameof('to')}
                    showErrorWhenNotTouched={true}
                  />
                </Grid>}
              <Grid item xs={12}>
                <FormikRelationSelect
                  iconName="hotel"
                  labelText="Host"
                  name={nameof("guestId")}
                  showErrorWhenNotTouched={false}
                  errorMessage={errorMessage}
                  items={guestsSelectItems as RelationSelectItem[]}
                  onChange={(newValue: number): void => {
                    props.getInfo({ ...values, guestId: newValue });
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormikRelationSelect
                  iconName="hotel"
                  labelText="Pokoj"
                  name="roomId"
                  showErrorWhenNotTouched={false}
                  errorMessage={errorMessage}
                  items={filteredRoomsSelectItems as RelationSelectItem[]}
                  onChange={(value: number): void => {
                    const room = filteredRoomsSelectItems.find(room => room.value === value);
                    setFieldValue('roomName', room?.text);
                  }}
                />
              </Grid>
              <Grid item xs={12} classes={{ root: classes.gridButtonsRoot }}>
                <Button variant="contained" title="Uložit" color="primary" type="submit" disabled={requestInProgress}>
                  Uložit
                </Button>

                <Button
                  variant="contained"
                  title="Zavřít"
                  color="secondary"
                  disabled={requestInProgress}
                  onClick={handleActionCancel}
                >
                  Zavřít
                </Button>
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Formik>
  );
};

const ReservationGuestCheckOutDetail: React.FunctionComponent<ReservationGuestCheckOutDetailProps> = memo(props => {
  console.debug('ReservationGuestCheckOutDetail');
  const {
    errorMessage,
    requestInProgress,
    initialValues,
    classes,
    modalDialog,
    modalDialogRoute,
    loadDependenciesRequest,
    saveActionRequest,
    cancelActionRequest,
    guestsSelectItems,
    roomsSelectItems,
    rooms,
    type,
    getInfo,
    info
  } = props;

  return (
    <BaseDetailContainer<ReservationGuestCheckOutModel>
      loadDependenciesRequest={loadDependenciesRequest}
      initialValues={initialValues}
      errorMessage={errorMessage}
      requestInProgress={requestInProgress}
      classes={classes}
      modalDialog={modalDialog}
      modalDialogRoute={modalDialogRoute}
      saveActionRequest={saveActionRequest}
      cancelActionRequest={cancelActionRequest}
      {...{
        guestsSelectItems: guestsSelectItems,
        roomsSelectItems: roomsSelectItems,
        rooms: rooms,
        type: type,
        getInfo: getInfo,
        info: info
      }}
    >
      {RenderFormComponent}
    </BaseDetailContainer>
  );
});

export default ReservationGuestCheckOutDetail;
